/**
 * 🍳 RecipeMatchPanel — "cook right now from your pantry" filter.
 *
 * Owner directive Feb 2026:
 *   "Recipe Match: Filter Interactive Kitchen recipes to what you can
 *    actually cook from your current pantry, and mark the missing 1-2
 *    items on the rest."
 *
 * Sections:
 *   🔥 Cook now  — 0 missing ingredients
 *   ⚡ Almost    — 1-2 missing (+1-tap "add missing to shopping list")
 *   ⋯ Later     — 3+ missing (collapsed by default)
 *
 * Placement: Interactive Kitchen page (below the pantry section).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ChefHat, Sparkles, ShoppingCart, ChevronDown, ChevronUp, Clock, Flame } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function IngPill({ ing, missing = false }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${
        missing
          ? "bg-rose-500/20 text-rose-200 border border-rose-500/40"
          : ing.optional
            ? "bg-white/5 text-white/40 border border-white/10 line-through"
            : "bg-emerald-500/15 text-emerald-200 border border-emerald-500/30"
      }`}
    >
      {ing.name}{ing.qty ? <span className="text-white/40"> · {ing.qty}</span> : null}
    </span>
  );
}

function RecipeCard({ recipe, onAddMissing, adding }) {
  const [expanded, setExpanded] = useState(false);
  const cookNow = recipe.missing_count === 0;
  const almost = recipe.missing_count > 0 && recipe.missing_count <= 2;
  return (
    <div
      className={`rounded-lg border p-3 transition ${
        cookNow
          ? "border-emerald-400/50 bg-gradient-to-br from-emerald-950/40 to-black"
          : almost
            ? "border-amber-400/40 bg-black/50"
            : "border-white/10 bg-black/40"
      }`}
      data-testid={`recipe-card-${recipe.recipe_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-black text-white truncate">{recipe.title}</div>
          <div className="text-[11px] text-white/60 flex items-center gap-2 mt-0.5">
            <span className="inline-flex items-center gap-0.5"><Clock size={9} /> {recipe.time_min} min</span>
            {recipe.cuisine && <span className="text-white/40">· {recipe.cuisine}</span>}
          </div>
        </div>
        <div className="shrink-0 text-right">
          {cookNow ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-black text-[10px] font-black uppercase tracking-widest">
              <Flame size={10} /> cook now
            </span>
          ) : (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
              almost ? "bg-amber-500/20 text-amber-200 border border-amber-400/50" : "bg-white/10 text-white/60 border border-white/15"
            }`}>
              missing {recipe.missing_count}
            </span>
          )}
        </div>
      </div>

      <div className="text-xs text-white/70 mt-1">{recipe.blurb}</div>

      {/* Ingredients summary */}
      <div className="mt-2 flex flex-wrap gap-1">
        {recipe.matches.slice(0, expanded ? 20 : 4).map((m) => (
          <IngPill key={`m-${m.catalog_id}`} ing={m} />
        ))}
        {recipe.missing.slice(0, expanded ? 20 : 3).map((m) => (
          <IngPill key={`x-${m.catalog_id}`} ing={m} missing />
        ))}
        {(recipe.matches.length + recipe.missing.length) > 7 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-[10px] text-white/50 hover:text-white/80"
            data-testid={`recipe-toggle-${recipe.recipe_id}`}
          >
            {expanded ? <><ChevronUp size={10} className="inline" /> less</> : <><ChevronDown size={10} className="inline" /> show all</>}
          </button>
        )}
      </div>

      {/* Steps preview */}
      {expanded && (
        <ol className="mt-2 space-y-1 list-decimal list-inside pl-1" data-testid={`recipe-steps-${recipe.recipe_id}`}>
          {recipe.steps.map((s, i) => (
            <li key={i} className="text-[11px] text-white/70">{s}</li>
          ))}
        </ol>
      )}

      {/* Action row */}
      {recipe.missing.length > 0 && (
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => onAddMissing(recipe)}
            disabled={adding}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest transition disabled:opacity-60 ${
              almost
                ? "bg-amber-500 text-black hover:bg-amber-400"
                : "bg-white/10 text-white hover:bg-white/20 border border-white/15"
            }`}
            data-testid={`recipe-shop-${recipe.recipe_id}`}
          >
            {adding ? <Loader2 size={10} className="animate-spin" /> : <ShoppingCart size={10} />}
            Add {recipe.missing.length} to shopping list
          </button>
        </div>
      )}
    </div>
  );
}

export function RecipeMatchPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLater, setShowLater] = useState(false);
  const [addingId, setAddingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/kitchen/recipe-match`, { withCredentials: true });
      setData(r.data || null);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onAddMissing = async (recipe) => {
    setAddingId(recipe.recipe_id);
    try {
      const r = await axios.post(`${API}/kitchen/recipe-match/${recipe.recipe_id}/shop-missing`, {}, { withCredentials: true });
      const added = r.data?.count || 0;
      const quotaHit = !!r.data?.quota_hit;
      if (added === 0 && !quotaHit) {
        toast.success("Already on your shopping list.", {
          action: { label: "View list", onClick: openShoppingDrawer },
        });
      } else if (quotaHit) {
        toast.warning(
          `Added ${added} — free plan hit its limit. Upgrade for unlimited.`,
          { action: { label: "View list", onClick: openShoppingDrawer } }
        );
      } else {
        toast.success(
          `Added ${added} item${added !== 1 ? "s" : ""} to shopping list — for ${recipe.title}.`,
          { action: { label: "View list", onClick: openShoppingDrawer } }
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add.");
    } finally {
      setAddingId(null);
    }
  };

  const openShoppingDrawer = () => {
    window.dispatchEvent(new CustomEvent("omnimuscle:open-shopping"));
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-white/10 bg-black/40 p-4 text-center text-white/60 text-sm">
        <Loader2 size={14} className="animate-spin inline mr-2" /> matching recipes to your pantry…
      </div>
    );
  }

  if (!data || data.recipes.length === 0) {
    return (
      <div className="rounded-xl border border-white/10 bg-black/40 p-4 text-xs text-white/60 italic text-center">
        No recipes available.
      </div>
    );
  }

  const empty = data.pantry_size === 0;

  return (
    <div className="rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-950/20 via-black to-black p-4 space-y-4" data-testid="recipe-match-panel">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-emerald-500/20 border border-emerald-400/40">
          <ChefHat size={16} className="text-emerald-300" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Recipe Match</div>
          <div className="text-lg font-black text-white leading-tight">Cook from your pantry</div>
          <div className="text-xs text-white/60">
            {empty
              ? "Add a few items to your pantry above and this fills in."
              : `Matching ${data.recipes.length} recipes against ${data.pantry_size} pantry item${data.pantry_size !== 1 ? "s" : ""}.`}
          </div>
        </div>
        <button
          type="button"
          onClick={openShoppingDrawer}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-black/50 border border-white/20 text-white text-[10px] font-black uppercase tracking-widest hover:bg-white/10"
          data-testid="recipe-match-open-shopping"
          title="Open Shopping List"
        >
          <ShoppingCart size={12} /> List
        </button>
      </div>

      {/* 🔥 Cook now */}
      {data.cook_now.length > 0 && (
        <section data-testid="recipe-match-cook-now">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold flex items-center gap-1">
              <Flame size={11} /> Cook right now · {data.cook_now.length}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.cook_now.map((r) => (
              <RecipeCard key={r.recipe_id} recipe={r} onAddMissing={onAddMissing} adding={addingId === r.recipe_id} />
            ))}
          </div>
        </section>
      )}

      {/* ⚡ Almost */}
      {data.almost.length > 0 && (
        <section data-testid="recipe-match-almost">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold flex items-center gap-1">
              <Sparkles size={11} /> Almost there · {data.almost.length}
              <span className="text-white/40 normal-case tracking-normal">— missing 1–2 items</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.almost.map((r) => (
              <RecipeCard key={r.recipe_id} recipe={r} onAddMissing={onAddMissing} adding={addingId === r.recipe_id} />
            ))}
          </div>
        </section>
      )}

      {/* Later — collapsed */}
      {data.later.length > 0 && (
        <section data-testid="recipe-match-later">
          <button
            type="button"
            onClick={() => setShowLater((v) => !v)}
            className="w-full flex items-center justify-between px-2 py-2 rounded bg-black/40 border border-white/10 hover:bg-white/5"
            data-testid="recipe-match-later-toggle"
          >
            <span className="text-[10px] uppercase tracking-widest text-white/60 font-bold">
              ⋯ Later · {data.later.length} more (need 3+ items)
            </span>
            {showLater ? <ChevronUp size={12} className="text-white/60" /> : <ChevronDown size={12} className="text-white/60" />}
          </button>
          {showLater && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
              {data.later.map((r) => (
                <RecipeCard key={r.recipe_id} recipe={r} onAddMissing={onAddMissing} adding={addingId === r.recipe_id} />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default RecipeMatchPanel;
